import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import api, { formatINR } from "@/lib/api";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, Legend } from "recharts";
import { Link } from "react-router-dom";

const COLORS = ["#0d9488", "#0f172a", "#0284c7", "#f59e0b", "#64748b"];

export default function AdminDashboard() {
  const [m, setM] = useState(null);
  useEffect(() => { api.get("/dashboard/admin").then(r => setM(r.data)); }, []);
  return (
    <div data-testid="admin-dashboard">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">Admin & Operations</h1>
        <p className="text-sm text-slate-600 mt-1">Red Fort Capital Finance — CarFin India MIS.</p>
      </div>

      <div className="space-y-5">
        <KpiGroup title="Volume">
          <Kpi label="Total applications" value={m?.total_applications} testId="kpi-total" />
          <Kpi label="Approved" value={m?.approved} testId="kpi-approved" tone="approved" />
          <Kpi label="Disbursed" value={m?.disbursed} testId="kpi-disbursed" tone="approved" />
          <Kpi label="Rejected" value={m?.rejected} testId="kpi-rejected" tone="rejected" />
          <Kpi label="Approval rate" value={m ? `${m.approval_rate}%` : null} testId="kpi-approval-rate" />
        </KpiGroup>
        <KpiGroup title="Money">
          <Kpi label="Sanctioned amount" value={formatINR(m?.sanctioned_amount)} testId="kpi-sanctioned-amount" tone="approved" />
          <Kpi label="Disbursed amount" value={formatINR(m?.disbursed_amount)} testId="kpi-disbursed-amount" tone="approved" />
        </KpiGroup>
        <KpiGroup title="Operational queues">
          <Kpi label="Pending underwriting" value={m?.pending_underwriting} testId="kpi-pending-uw" tone="review" />
          <Kpi label="Pending documents" value={m?.pending_documents} testId="kpi-pending-docs" tone="review" />
          <Kpi label="Pending CarDr" value={m?.pending_cardr} testId="kpi-pending-cardr" tone="review" />
        </KpiGroup>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
        <Card className="p-5 border-slate-200">
          <div className="text-sm font-semibold text-slate-900 mb-3">Applications by city</div>
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={m?.by_city || []}>
                <XAxis dataKey="city" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#0d9488" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card className="p-5 border-slate-200">
          <div className="text-sm font-semibold text-slate-900 mb-3">Applications by source</div>
          <div className="h-64">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={m?.by_source || []} dataKey="count" nameKey="source" outerRadius={80} label>
                  {(m?.by_source || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <div className="mt-6">
        <Link to="/admin/applications" className="text-teal-700 font-medium" data-testid="admin-go-applications">Open applications →</Link>
      </div>
    </div>
  );
}

function Kpi({ label, value, testId, tone }) {
  const toneClass = tone === "approved" ? "text-teal-700" : tone === "review" ? "text-amber-700" : tone === "rejected" ? "text-red-700" : "text-slate-900";
  return (
    <Card className="p-5 border-slate-200" data-testid={testId}>
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className={"text-2xl font-semibold mt-1 font-mono " + toneClass}>{value ?? "—"}</div>
    </Card>
  );
}

function KpiGroup({ title, children }) {
  const arr = Array.isArray(children) ? children : [children];
  const cols = arr.length <= 2 ? "md:grid-cols-2" : arr.length === 3 ? "md:grid-cols-3" : arr.length === 4 ? "md:grid-cols-4" : "md:grid-cols-5";
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">{title}</div>
      <div className={"grid grid-cols-2 " + cols + " gap-3"}>{children}</div>
    </div>
  );
}
